import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Server, Cpu, Globe } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useTranslation } from './I18nContext';

export const Footer: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  return (
    <footer className="bg-white dark:bg-[#020617] border-t border-slate-200 dark:border-white/10 pt-16 pb-8 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">

          {/* Brand Col */}
          <div className="space-y-4">
            <Link to="/">
              <img
                src="/Grafx/henselx_web_primary.png"
                alt="HenselX Logo"
                className={`h-10 w-auto ${theme === 'dark' ? 'hidden' : 'block'}`}
              />
              <img
                src="/Grafx/henselx_web_inverted.png"
                alt="HenselX Logo"
                className={`h-10 w-auto ${theme === 'dark' ? 'block' : 'hidden'}`}
              />
            </Link>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
              {t('footer.tagline')}
              <br />
              {t('footer.tagline2')}
            </p>
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-4">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              {t('footer.status')}
            </div>
            <img
              src="/Grafx/henselx_hosted_in_acri.png"
              alt="Proudly Hosted in Acri – Natura & Comunità"
              className="h-36 w-auto mt-6 rounded-xl opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-300"
            />
          </div>

          {/* Services Col */}
          <div>
            <h4 className="text-slate-900 dark:text-white font-semibold mb-6">{t('footer.services')}</h4>
            <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
              <li><Link to="/services/infrastructure" className="hover:text-[#0ea5e9] transition-colors">{t('nav.infrastructure')}</Link></li>
              <li><Link to="/services/ai" className="hover:text-[#0ea5e9] transition-colors">Local LLMs & AI</Link></li>
              <li><Link to="/services/web" className="hover:text-[#0ea5e9] transition-colors">{t('nav.web')}</Link></li>
              <li><Link to="/contact" className="hover:text-[#0ea5e9] transition-colors">Consulting</Link></li>
            </ul>
          </div>

          {/* Legal Col */}
          <div>
            <h4 className="text-slate-900 dark:text-white font-semibold mb-6">{t('footer.legalTitle')}</h4>
            <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
              <li><Link to="/impressum" className="hover:text-[#0ea5e9] transition-colors">{t('footer.legalImprint')}</Link></li>
              <li><Link to="/datenschutz" className="hover:text-[#0ea5e9] transition-colors">{t('footer.legalPrivacy')}</Link></li>
              <li><Link to="/terms" className="hover:text-[#0ea5e9] transition-colors">{t('footer.legalTerms')}</Link></li>
            </ul>
          </div>

          {/* Contact Snippet — schema.org LocalBusiness NAP */}
          <div>
            <h4 className="text-slate-900 dark:text-white font-semibold mb-6">{t('footer.hqTitle')}</h4>
            <address
              className="not-italic text-sm text-slate-600 dark:text-slate-400 space-y-2"
              itemScope
              itemType="https://schema.org/LocalBusiness"
            >
              <meta itemProp="name" content="HenselX" />
              <meta itemProp="url" content="https://henselx.it" />
              <p itemProp="employee" itemScope itemType="https://schema.org/Person">
                <span itemProp="name">Marc Hensel</span>
              </p>
              <p>
                <span itemProp="streetAddress">Contrada Aria delle Donne 39</span>
              </p>
              <p>
                <span itemProp="postalCode">87041</span>{' '}
                <span itemProp="addressLocality">Acri (CS)</span>,{' '}
                <span itemProp="addressCountry">Italia</span>
              </p>
              <a
                href="mailto:hello@henselx.io"
                itemProp="email"
                className="block text-slate-900 dark:text-white mt-4 hover:text-[#0ea5e9] transition-colors"
              >
                hello@henselx.io
              </a>
              <a
                href="tel:+393242511984"
                itemProp="telephone"
                className="block hover:text-[#0ea5e9] transition-colors"
              >
                +39 324 2511 984
              </a>
              <p className="pt-1 text-xs">P.IVA: IT02110060890</p>
            </address>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 dark:text-slate-600">
          <p>&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <ShieldCheck className="w-4 h-4" />
            <Server className="w-4 h-4" />
            <Cpu className="w-4 h-4" />
            <Globe className="w-4 h-4" />
          </div>
        </div>
      </div>
    </footer>
  );
};
